import { ArrowDownLeft, ArrowRightLeft, ArrowUpRight, Lock, Trash2, Undo2, Unlock } from "lucide-react";

import type { InventoryTransaction, TransactionType } from "@/api/types";
import { EmptyState } from "@/components/States";
import { StatusBadge } from "@/components/StatusBadge";
import {
  formatDateTime,
  formatQuantity,
  formatReasonCode,
  formatTransactionType,
  formatUnit,
  isNegativeQuantity,
  isZeroQuantity,
} from "@/lib/format";

const ICONS: Record<TransactionType, typeof ArrowDownLeft> = {
  RECEIVE: ArrowDownLeft,
  RETURN: ArrowDownLeft,
  ISSUE: ArrowUpRight,
  CONSUME: ArrowUpRight,
  SCRAP: Trash2,
  TRANSFER: ArrowRightLeft,
  ADJUSTMENT: Undo2,
  RESERVE: Lock,
  RELEASE_RESERVATION: Unlock,
  SUPPLIER_RETURN: ArrowUpRight,
};

/**
 * The movement list (TICKET FE-010).
 *
 * Every line is a ledger entry. A reversal shows what it cancels rather than
 * making the original disappear — history is never rewritten.
 */
export function MovementList({ transactions }: { transactions: InventoryTransaction[] }) {
  if (transactions.length === 0) {
    return (
      <EmptyState
        title="Aucun mouvement"
        description="Réceptions, sorties, transferts, ajustements et réservations apparaîtront ici."
      />
    );
  }

  return (
    <ul className="divide-y divide-border/60">
      {transactions.map((transaction) => {
        const Icon = ICONS[transaction.transaction_type] ?? ArrowRightLeft;
        const isReservation = !isZeroQuantity(transaction.reserved_delta);
        const amount = isReservation ? transaction.reserved_delta : transaction.quantity_delta;
        const negative = isNegativeQuantity(amount);

        return (
          <li key={transaction.id} className="flex items-start gap-3 px-4 py-3">
            <span
              className={
                negative
                  ? "mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-md bg-danger-subtle text-status-out"
                  : "mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-md bg-success-subtle text-status-available"
              }
            >
              <Icon className="size-4" aria-hidden />
            </span>

            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-[13px] font-medium text-ink">
                  {formatTransactionType(transaction.transaction_type)}
                </span>
                <span className="text-[12px] text-ink-subtle">
                  {formatReasonCode(transaction.reason_code)}
                </span>
                {transaction.reverses_transaction_id && (
                  <StatusBadge tone="warning">Contre-passation</StatusBadge>
                )}
                {transaction.idempotency_key && (
                  <span
                    className="font-mono text-[10px] text-ink-subtle"
                    title="Clé d'idempotence : un rejeu de cette requête ne rejoue pas le mouvement."
                  >
                    {transaction.idempotency_key}
                  </span>
                )}
              </div>
              <p className="text-[11px] text-ink-subtle">
                {formatDateTime(transaction.created_at)} · {transaction.created_by_label}
              </p>
              {typeof transaction.metadata_json.book_quantity_before === "string" && (
                <p className="text-[11px] text-ink-subtle">
                  {formatQuantity(String(transaction.metadata_json.book_quantity_before))} →{" "}
                  {formatQuantity(String(transaction.metadata_json.book_quantity_after ?? ""))}
                </p>
              )}
            </div>

            <div className="shrink-0 text-right">
              <p
                className={
                  negative
                    ? "tnum text-[15px] font-semibold text-status-out"
                    : "tnum text-[15px] font-semibold text-status-available"
                }
              >
                {negative ? "" : "+"}
                {formatQuantity(amount)}
              </p>
              <p className="text-[10px] text-ink-subtle">
                {isReservation ? "réservé" : formatUnit(transaction.unit)}
              </p>
            </div>
          </li>
        );
      })}
    </ul>
  );
}
